import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MarkupConfig, ClientBilling } from "@/types/billing";
import { Percent, DollarSign, Phone, MessageSquare, FileText, Bot, Link, ChevronRight, Building2, User, AlertCircle, RotateCcw } from "lucide-react";

interface MarkupConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: ClientBilling | null;
  existingConfig: MarkupConfig | null;
  defaultMarkup: number;
  onSave: (config: Partial<MarkupConfig>) => void;
  onResetToDefault?: (tenantId: string) => void;
  isSaving?: boolean;
}

export function MarkupConfigDialog({
  open,
  onOpenChange,
  client,
  existingConfig,
  defaultMarkup,
  onSave,
  onResetToDefault,
  isSaving,
}: MarkupConfigDialogProps) {
  const [markupPercentage, setMarkupPercentage] = useState(
    existingConfig?.markup_percentage ?? defaultMarkup
  );
  const [useCustomPerService, setUseCustomPerService] = useState(
    !!(existingConfig?.voice_markup || existingConfig?.sms_markup ||
       existingConfig?.transcription_markup || existingConfig?.ai_markup)
  );
  const [voiceMarkup, setVoiceMarkup] = useState(
    existingConfig?.voice_markup ?? markupPercentage
  );
  const [smsMarkup, setSmsMarkup] = useState(
    existingConfig?.sms_markup ?? markupPercentage
  );
  const [transcriptionMarkup, setTranscriptionMarkup] = useState(
    existingConfig?.transcription_markup ?? markupPercentage
  );
  const [aiMarkup, setAiMarkup] = useState(
    existingConfig?.ai_markup ?? markupPercentage
  );

  // Calculate preview amounts based on client's usage
  const previewAmounts = useMemo(() => {
    if (!client?.usage_summary) return null;

    const usage = client.usage_summary;
    const effectiveVoice = useCustomPerService ? voiceMarkup : markupPercentage;
    const effectiveSms = useCustomPerService ? smsMarkup : markupPercentage;
    const effectiveTranscription = useCustomPerService ? transcriptionMarkup : markupPercentage;
    const effectiveAi = useCustomPerService ? aiMarkup : markupPercentage;

    const voiceMarkupAmount = usage.voice_cost * (effectiveVoice / 100);
    const smsMarkupAmount = usage.sms_cost * (effectiveSms / 100);
    const transcriptionMarkupAmount = usage.transcription_cost * (effectiveTranscription / 100);
    const aiMarkupAmount = usage.ai_cost * (effectiveAi / 100);

    const totalMarkup = voiceMarkupAmount + smsMarkupAmount + transcriptionMarkupAmount + aiMarkupAmount;
    const totalBilled = usage.total_cost + totalMarkup;

    return {
      voice: voiceMarkupAmount,
      sms: smsMarkupAmount,
      transcription: transcriptionMarkupAmount,
      ai: aiMarkupAmount,
      totalMarkup,
      totalBilled,
    };
  }, [client, markupPercentage, voiceMarkup, smsMarkup, transcriptionMarkup, aiMarkup, useCustomPerService]);

  const handleSave = () => {
    const config: Partial<MarkupConfig> = {
      tenant_id: client?.tenant_id || null,
      markup_percentage: markupPercentage,
    };

    if (useCustomPerService) {
      config.voice_markup = voiceMarkup;
      config.sms_markup = smsMarkup;
      config.transcription_markup = transcriptionMarkup;
      config.ai_markup = aiMarkup;
    }

    onSave(config);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {client ? `Markup for ${client.tenant_name}` : "Default Markup Settings"}
          </DialogTitle>
          <DialogDescription>
            {client
              ? "Configure the re-billing markup for this specific client."
              : "Set the default markup percentage applied to all clients."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Effective Rate Banner for Clients */}
          {client && (
            <div className={`rounded-lg p-4 ${existingConfig ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Percent className={`h-5 w-5 ${existingConfig ? 'text-blue-600' : 'text-green-600'}`} />
                  <div>
                    <p className="text-sm font-medium">Effective Markup Rate</p>
                    <p className="text-xs text-muted-foreground">
                      {existingConfig ? 'Custom rate for this client' : 'Inherited from agency default'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-2xl font-bold ${existingConfig ? 'text-blue-600' : 'text-green-600'}`}>
                    {existingConfig?.markup_percentage ?? defaultMarkup}%
                  </span>
                  <Badge
                    variant={existingConfig ? "outline" : "secondary"}
                    className={`ml-2 ${existingConfig ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}`}
                  >
                    {existingConfig ? 'Custom' : 'Inherited'}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Visual Inheritance Tree */}
          {client && (
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="text-xs font-medium text-muted-foreground mb-3">Settings Inheritance</div>
              <div className="space-y-2">
                {/* Agency Default Level */}
                <div className="flex items-center gap-2">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${!existingConfig ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Agency Default</span>
                      <Badge variant="secondary" className="text-xs">{defaultMarkup}%</Badge>
                      {!existingConfig && (
                        <Badge className="text-xs bg-green-100 text-green-800">Active</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Applied to all clients without custom settings</p>
                  </div>
                </div>

                {/* Connector Line */}
                <div className="flex items-center gap-2 pl-4">
                  <div className="w-px h-4 bg-border ml-3" />
                </div>

                {/* Client Override Level */}
                <div className="flex items-center gap-2 pl-6">
                  <ChevronRight className="h-4 w-4 text-muted-foreground -ml-4" />
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${existingConfig ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground border-2 border-dashed'}`}>
                    <User className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{client.tenant_name}</span>
                      {existingConfig ? (
                        <>
                          <Badge variant="outline" className="text-xs">{existingConfig.markup_percentage}%</Badge>
                          <Badge className="text-xs bg-blue-100 text-blue-800">Custom</Badge>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">No override</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {existingConfig
                        ? "Custom settings override agency default"
                        : "Inherits from agency default"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Markup Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="markup" className="flex items-center gap-2">
                <Percent className="h-4 w-4" />
                {useCustomPerService ? "Base Markup" : "Markup Percentage"}
              </Label>
              <span className="text-lg font-bold text-primary">{markupPercentage}%</span>
            </div>
            <Slider
              id="markup"
              value={[markupPercentage]}
              onValueChange={([value]) => setMarkupPercentage(value)}
              min={0}
              max={100}
              step={1}
              className="py-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Per-Service Toggle */}
          <div className="flex items-center justify-between py-2 border-t border-b">
            <div className="space-y-0.5">
              <Label htmlFor="per-service">Custom per-service markup</Label>
              <p className="text-xs text-muted-foreground">
                Set different markup rates for each service type
              </p>
            </div>
            <Switch
              id="per-service"
              checked={useCustomPerService}
              onCheckedChange={(checked) => {
                setUseCustomPerService(checked);
                if (!checked) {
                  setVoiceMarkup(markupPercentage);
                  setSmsMarkup(markupPercentage);
                  setTranscriptionMarkup(markupPercentage);
                  setAiMarkup(markupPercentage);
                }
              }}
            />
          </div>

          {/* Per-Service Markup Controls */}
          {useCustomPerService && (
            <div className="grid gap-4">
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                <Label className="w-28 shrink-0">Voice Calls</Label>
                <Input
                  type="number"
                  min={0}
                  max={200}
                  value={voiceMarkup}
                  onChange={(e) => setVoiceMarkup(Number(e.target.value))}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <div className="flex items-center gap-3">
                <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                <Label className="w-28 shrink-0">SMS Messages</Label>
                <Input
                  type="number"
                  min={0}
                  max={200}
                  value={smsMarkup}
                  onChange={(e) => setSmsMarkup(Number(e.target.value))}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <Label className="w-28 shrink-0">Transcription</Label>
                <Input
                  type="number"
                  min={0}
                  max={200}
                  value={transcriptionMarkup}
                  onChange={(e) => setTranscriptionMarkup(Number(e.target.value))}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <div className="flex items-center gap-3">
                <Bot className="h-4 w-4 text-muted-foreground shrink-0" />
                <Label className="w-28 shrink-0">AI Analysis</Label>
                <Input
                  type="number"
                  min={0}
                  max={200}
                  value={aiMarkup}
                  onChange={(e) => setAiMarkup(Number(e.target.value))}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </div>
          )}

          {/* Preview Card */}
          {client && previewAmounts && (
            <Card className="bg-muted/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Preview (Current Period)
                </CardTitle>
                <CardDescription>
                  Based on {client.tenant_name}'s current usage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base Cost</span>
                  <span>{formatCurrency(client.base_cost)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Markup Amount</span>
                  <span>+{formatCurrency(previewAmounts.totalMarkup)}</span>
                </div>
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>Total Billed</span>
                  <span>{formatCurrency(previewAmounts.totalBilled)}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {client && existingConfig && onResetToDefault && (
            <Button
              variant="outline"
              onClick={() => onResetToDefault(client.tenant_id)}
              disabled={isSaving}
              className="sm:mr-auto border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Inherited
            </Button>
          )}
          {client && !existingConfig && (
            <Alert className="sm:mr-auto p-2 border-green-200 bg-green-50">
              <AlertDescription className="text-xs text-green-700 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Saving will create a custom override for this client
              </AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
